import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Input } from '../../../../components/ui';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  XCircleIcon as XCircle, 
  ArrowTrendingUpIcon as TrendingUp, 
  ArrowTrendingDownIcon as TrendingDown, 
  UsersIcon as Users, 
  BoltIcon as Activity,
  ClockIcon as Clock,
  CheckCircleIcon as CheckCircle,
  PauseIcon as Pause,
  PlayIcon as Play
} from '@heroicons/react/24/outline';
import { MagnifyingGlassIcon as Search } from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../../../config/api';

interface OrganizationUsage {
  organization_id: number;
  organization_name: string;
  total_requests: number;
  total_success: number;
  total_errors: number;
  success_rate: number;
  avg_response_time: number;
  quota_usage_percentage: number;
  top_endpoints: Array<{
    endpoint: string;
    request_count: number;
    success_rate: number;
    avg_response_time: number;
  }>;
  error_breakdown: Record<string, number>;
  daily_trend: Array<{
    date: string;
    request_count: number;
    avg_response_time: number;
  }>;
  category_breakdown: Record<string, number>;
}

interface QuotaViolation {
  organization_id: number;
  organization_name: string;
  daily_exceeded: boolean;
  monthly_exceeded: boolean;
  warning_threshold_reached: boolean;
  is_suspended: boolean;
  usage_percentage: number;
  daily_usage: number;
  monthly_usage: number;
  daily_limit: number;
  monthly_limit: number;
}

interface SystemStats {
  total_requests: number;
  success_requests: number;
  error_requests: number;
  success_rate: number;
  avg_response_time_ms: number;
  active_organizations: number;
  category_breakdown: Record<string, number>;
  hourly_trend: Array<{
    hour: string;
    count: number;
  }>;
}

const ApiUsageMonitoring: React.FC = () => {
  const [organizationsUsage, setOrganizationsUsage] = useState<OrganizationUsage[]>([]);
  const [quotaViolations, setQuotaViolations] = useState<QuotaViolation[]>([]);
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [periodHours, setPeriodHours] = useState(24);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);

  // 데이터 로딩
  const loadData = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // 모든 회사 사용량 조회
      const orgResponse = await fetch(`${API_BASE_URL}/admin/monitoring/organizations`, {
        headers
      });
      
      if (orgResponse.ok) {
        const orgData = await orgResponse.json();
        setOrganizationsUsage(orgData);
      }

      // 할당량 위반 조회
      const violationsResponse = await fetch(`${API_BASE_URL}/admin/monitoring/quota-violations`, {
        headers
      });
      
      if (violationsResponse.ok) {
        const violationsData = await violationsResponse.json();
        setQuotaViolations(violationsData);
      }

      // 시스템 통계 조회
      const statsResponse = await fetch(`${API_BASE_URL}/admin/monitoring/system-stats?period_hours=${periodHours}`, {
        headers
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setSystemStats(statsData);
      }

    } catch (error) {
      console.error('Failed to load API usage data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    
    // 자동 새로고침 설정 (5분마다)
    const interval = setInterval(loadData, 5 * 60 * 1000);
    setRefreshInterval(interval);
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [periodHours]);

  // 회사 정지/재개
  const handleSuspendOrganization = async (organizationId: number, suspend: boolean) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const action = suspend ? 'suspend' : 'resume';
      const url = `${API_BASE_URL}/admin/monitoring/organizations/${organizationId}/${action}`;
      const body = suspend ? { reason: 'Quota exceeded' } : {};

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (response.ok) {
        await loadData(); // 데이터 새로고침
      }
    } catch (error) {
      console.error(`Failed to ${suspend ? 'suspend' : 'resume'} organization:`, error);
    }
  };

  // 필터링된 회사 목록
  const filteredOrganizations = organizationsUsage.filter(org =>
    org.organization_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 차트 색상
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Activity className="h-8 w-8 animate-spin mx-auto mb-2" />
          <p>로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">API 사용량 모니터링</h1>
        <div className="flex items-center space-x-4">
          <select 
            value={periodHours.toString()} 
            onChange={(e) => setPeriodHours(parseInt(e.target.value))}
            className="w-32 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="24">24시간</option>
            <option value="72">3일</option>
            <option value="168">7일</option>
          </select>
          <Button onClick={loadData} variant="outline" size="sm">
            새로고침
          </Button>
        </div>
      </div>

      {/* 시스템 전체 통계 */}
      {systemStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <Card.Content className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">총 요청 수</p>
                  <p className="text-2xl font-bold">{systemStats.total_requests.toLocaleString()}</p>
                </div>
                <Activity className="h-8 w-8 text-blue-600" />
              </div>
            </Card.Content>
          </Card>

          <Card>
            <Card.Content className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">성공률</p>
                  <p className="text-2xl font-bold text-green-600">{systemStats.success_rate}%</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </Card.Content>
          </Card>

          <Card>
            <Card.Content className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">평균 응답시간</p>
                  <p className="text-2xl font-bold">{systemStats.avg_response_time_ms.toFixed(1)}ms</p>
                </div>
                <Clock className="h-8 w-8 text-orange-600" />
              </div>
            </Card.Content>
          </Card>

          <Card>
            <Card.Content className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">활성 회사</p>
                  <p className="text-2xl font-bold">{systemStats.active_organizations}</p>
                </div>
                <Users className="h-8 w-8 text-purple-600" />
              </div>
            </Card.Content>
          </Card>
        </div>
      )}

      {/* 할당량 위반 알림 */}
      {quotaViolations.length > 0 && (
        <Card className="border-red-200">
          <Card.Header className="bg-red-50">
            <Card.Title className="flex items-center text-red-800">
              <XCircle className="h-5 w-5 mr-2" />
              할당량 위반 ({quotaViolations.length}개 회사)
            </Card.Title>
          </Card.Header>
          <Card.Content className="p-6">
            <div className="space-y-3">
              {quotaViolations.map((violation) => (
                <div key={violation.organization_id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div>
                    <p className="font-medium">{violation.organization_name}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>사용률: {violation.usage_percentage.toFixed(1)}%</span>
                      <span>월간: {violation.monthly_usage.toLocaleString()}/{violation.monthly_limit.toLocaleString()}</span>
                      {violation.daily_exceeded && <Badge variant="destructive">일일 초과</Badge>}
                      {violation.monthly_exceeded && <Badge variant="destructive">월간 초과</Badge>}
                      {violation.is_suspended && <Badge variant="outline">정지됨</Badge>}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant={violation.is_suspended ? "primary" : "secondary"}
                      size="sm"
                      onClick={() => handleSuspendOrganization(violation.organization_id, !violation.is_suspended)}
                    >
                      {violation.is_suspended ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                      {violation.is_suspended ? '재개' : '정지'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card.Content>
        </Card>
      )}

      {/* 시간별 트렌드 차트 */}
      {systemStats && (
        <Card>
          <Card.Header>
            <Card.Title>시간별 API 요청 트렌드</Card.Title>
          </Card.Header>
          <Card.Content>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={systemStats.hourly_trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="hour" 
                  tickFormatter={(value) => new Date(value).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleString('ko-KR')}
                  formatter={(value) => [value, '요청 수']}
                />
                <Line type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card.Content>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 회사별 사용량 목록 */}
        <Card>
          <Card.Header>
            <Card.Title>회사별 API 사용량</Card.Title>
            <div className="flex items-center space-x-2">
              <Search className="h-4 w-4 text-gray-400" />
              <Input
                placeholder="회사명 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
              />
            </div>
          </Card.Header>
          <Card.Content>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredOrganizations.map((org) => (
                <div 
                  key={org.organization_id} 
                  className={`p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                    selectedOrg?.organization_id === org.organization_id ? 'border-blue-500 bg-blue-50' : ''
                  }`}
                  onClick={() => setSelectedOrg(org)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{org.organization_name}</p>
                      <div className="text-sm text-gray-600">
                        <span>요청: {org.total_requests.toLocaleString()}</span>
                        <span className="ml-4">성공률: {org.success_rate}%</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={org.success_rate >= 95 ? 'default' : 'destructive'}>
                        {org.avg_response_time.toFixed(0)}ms
                      </Badge>
                      <div className="text-xs text-gray-500 mt-1">
                        할당량: {org.quota_usage_percentage.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card.Content>
        </Card>

        {/* 카테고리별 사용량 */}
        {systemStats && (
          <Card>
            <Card.Header>
              <Card.Title>카테고리별 API 사용량</Card.Title>
            </Card.Header>
            <Card.Content>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={Object.entries(systemStats.category_breakdown).map(([name, value]) => ({ name, value }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.entries(systemStats.category_breakdown).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card.Content>
          </Card>
        )}
      </div>

      {/* 선택된 회사 상세 정보 */}
      {selectedOrg && (
        <Card>
          <Card.Header>
            <Card.Title>{selectedOrg.organization_name} 상세 분석</Card.Title>
          </Card.Header>
          <Card.Content>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 일별 트렌드 */}
              <div>
                <h4 className="text-lg font-medium mb-4">일별 요청 트렌드</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={selectedOrg.daily_trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="request_count" stroke="#8884d8" name="요청 수" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* 상위 엔드포인트 */}
              <div>
                <h4 className="text-lg font-medium mb-4">상위 엔드포인트</h4>
                <div className="space-y-2">
                  {selectedOrg.top_endpoints.slice(0, 5).map((endpoint, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <span className="text-sm font-mono">{endpoint.endpoint}</span>
                      <div className="text-right text-sm">
                        <div>{endpoint.request_count.toLocaleString()}</div>
                        <div className="text-gray-500">{endpoint.success_rate.toFixed(1)}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card.Content>
        </Card>
      )}
    </div>
  );
};

export default ApiUsageMonitoring;